import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, ArrowLeft, Globe } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

export const PrivacyPage = () => {
  const { t, isRTL, language, setLanguage, supportedLanguages } = useLanguage();

  return (
    <div className={`min-h-screen bg-background`} dir={isRTL ? 'rtl' : 'ltr'}>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-4xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
              <MapPin className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="font-heading font-bold text-xl text-foreground">{t("appName")}</span>
          </Link>
          <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="w-[130px]">
                <Globe className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {supportedLanguages.map((lang) => (
                  <SelectItem key={lang.code} value={lang.code}>
                    {lang.nativeName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Link to="/">
              <Button variant="ghost" size="sm" className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                <ArrowLeft className={`w-4 h-4 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} />{t("backToHome")}
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <main className="pt-24 pb-16 px-4 md:px-8">
        <div className={`max-w-4xl mx-auto prose prose-gray dark:prose-invert ${isRTL ? 'text-right' : 'text-left'}`}>
          <h1 className="font-heading text-4xl font-bold mb-8">{t("privacyPolicy")}</h1>
          
          <p className="text-muted-foreground mb-8">{t("lastUpdated")}: {t("privacyLastUpdateDate")}</p>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">{t("privacy.section1Title")}</h2>
            <p>{t("privacy.section1Intro")}</p>
            
            <h3 className="text-xl font-semibold mt-6 mb-3">{t("privacy.personalInfo")}</h3>
            <ul className={`${isRTL ? 'list-disc pr-6' : 'list-disc pl-6'} space-y-2`}>
              <li>{t("privacy.personalInfo1")}</li>
              <li>{t("privacy.personalInfo2")}</li>
              <li>{t("privacy.personalInfo3")}</li>
              <li>{t("privacy.personalInfo4")}</li>
            </ul>

            <h3 className="text-xl font-semibold mt-6 mb-3">{t("privacy.locationData")}</h3>
            <ul className={`${isRTL ? 'list-disc pr-6' : 'list-disc pl-6'} space-y-2`}>
              <li>{t("privacy.locationData1")}</li>
              <li>{t("privacy.locationData2")}</li>
              <li>{t("privacy.locationData3")}</li>
            </ul>

            <h3 className="text-xl font-semibold mt-6 mb-3">{t("privacy.technicalData")}</h3>
            <ul className={`${isRTL ? 'list-disc pr-6' : 'list-disc pl-6'} space-y-2`}>
              <li>{t("privacy.technicalData1")}</li>
              <li>{t("privacy.technicalData2")}</li>
              <li>{t("privacy.technicalData3")}</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">{t("privacy.section2Title")}</h2>
            <ul className={`${isRTL ? 'list-disc pr-6' : 'list-disc pl-6'} space-y-2`}>
              <li>{t("privacy.howWeUse1")}</li>
              <li>{t("privacy.howWeUse2")}</li>
              <li>{t("privacy.howWeUse3")}</li>
              <li>{t("privacy.howWeUse4")}</li>
              <li>{t("privacy.howWeUse5")}</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">{t("privacy.section3Title")}</h2>
            <p>{t("privacy.dataShareIntro")}</p>
            <ul className={`${isRTL ? 'list-disc pr-6' : 'list-disc pl-6'} mt-4 space-y-2`}>
              <li>{t("privacy.dataShare1")}</li>
              <li>{t("privacy.dataShare2")}</li>
              <li>{t("privacy.dataShare3")}</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">{t("privacy.section4Title")}</h2>
            <p>{t("privacy.dataRetention")}</p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">{t("privacy.section5Title")}</h2>
            <p>{t("privacy.dataSecurityIntro")}</p>
            <ul className={`${isRTL ? 'list-disc pr-6' : 'list-disc pl-6'} mt-4 space-y-2`}>
              <li>{t("privacy.dataSecurity1")}</li>
              <li>{t("privacy.dataSecurity2")}</li>
              <li>{t("privacy.dataSecurity3")}</li>
              <li>{t("privacy.dataSecurity4")}</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">{t("privacy.section6Title")}</h2>
            <p>{t("privacy.yourRightsIntro")}</p>
            <ul className={`${isRTL ? 'list-disc pr-6' : 'list-disc pl-6'} mt-4 space-y-2`}>
              <li>{t("privacy.yourRights1")}</li>
              <li>{t("privacy.yourRights2")}</li>
              <li>{t("privacy.yourRights3")}</li>
              <li>{t("privacy.yourRights4")}</li>
              <li>{t("privacy.yourRights5")}</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">{t("privacy.section7Title")}</h2>
            <p>{t("privacy.locationPermissions")}</p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">{t("privacy.section8Title")}</h2>
            <p>{t("privacy.policyChanges")}</p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">{t("privacy.section9Title")}</h2>
            <p>{t("privacy.contactIntro")}</p>
            <p className="mt-2">{t("email")}: geocheck.inc@gmail.com</p>
          </section>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPage;
